import React, { useState } from 'react';
import {
  Box,
  Container,
  SimpleGrid,
  VStack,
  HStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Icon,
  Badge,
  useToast,
  Select,
} from '@chakra-ui/react';
import {
  FiMapPin,
  FiPhone,
  FiMail,
  FiClock,
  FiSend,
  FiShield,
  FiBookOpen,
  FiMonitor,
  FiMessageSquare,
} from 'react-icons/fi';

function Contactus() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    message: '',
    inquiryType: 'General Inquiry',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setFormData({ name: '', email: '', mobile: '', message: '', inquiryType: 'General Inquiry' });
      toast({
        title: 'Inquiry Submitted Successfully',
        description: 'Thank you for reaching out. Our legal concierge will contact you within 24 business hours.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }, 1500);
  };

  return (
    <Box bg="#F8FAFC" minH="100vh" py={{ base: 8, md: 12 }}>
      {/* Hero Header */}
      <Box
        bg="linear-gradient(135deg, #0B132B 0%, #162237 60%, #1E293B 100%)"
        color="white"
        py={{ base: 12, md: 16 }}
        mb={{ base: 8, md: 12 }}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top="-50%"
          right="-10%"
          w="600px"
          h="600px"
          bg="radial-gradient(circle, rgba(234, 179, 8, 0.08) 0%, transparent 70%)"
          pointerEvents="none"
        />
        <Container maxW="7xl" px={{ base: 4, md: 8 }} position="relative" zIndex={1}>
          <VStack spacing={4} align="center" textAlign="center" maxW="3xl" mx="auto">
            <Badge
              bg="rgba(234, 179, 8, 0.15)"
              color="#FACC15"
              border="1px solid"
              borderColor="rgba(234, 179, 8, 0.3)"
              px={3.5}
              py={1}
              borderRadius="full"
              fontSize="xs"
              fontWeight="semibold"
              letterSpacing="wider"
              textTransform="uppercase"
            >
              Legal Concierge & Support
            </Badge>
            <Heading as="h1" size={{ base: 'xl', md: '2xl' }} fontWeight="extrabold" letterSpacing="tight">
              Get in Touch with Navneet Law Studios
            </Heading>
            <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.300" lineHeight="tall">
              Whether you need assistance sourcing rare law commentaries, bespoke advocate apparel, or activating judicial research software, our team is at your service.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Main Content Grid */}
      <Container maxW="7xl" px={{ base: 4, md: 8 }}>
        <SimpleGrid columns={{ base: 1, lg: 12 }} spacing={{ base: 8, lg: 10 }}>
          {/* Left Column: Chamber Details & Direct Channels */}
          <Box gridColumn={{ base: 'span 1', lg: 'span 5' }}>
            <VStack spacing={6} align="stretch">
              <Box bg="white" p={{ base: 6, md: 8 }} borderRadius="2xl" border="1px solid" borderColor="gray.100" boxShadow="sm">
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" color="#EAB308" mb={2}>
                  Direct Contact Information
                </Text>
                <Heading as="h2" size="md" color="#162237" mb={4}>
                  Chambers & Bookstore Headquarters
                </Heading>
                <Text fontSize="sm" color="gray.600" mb={6} lineHeight="tall">
                  Visit our physical flagship showroom or reach our specialized departments directly during court hours.
                </Text>

                <VStack spacing={5} align="stretch">
                  <HStack align="flex-start" spacing={4}>
                    <Box p={3} bg="blue.50" color="#162237" borderRadius="xl">
                      <Icon as={FiMapPin} boxSize={5} />
                    </Box>
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">
                        Address
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="#162237">
                        Navneet Law Studios & Publications
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        Commercial Chambers Complex, Near High Court Metro Station, New Delhi – 110001, India
                      </Text>
                    </Box>
                  </HStack>

                  <HStack align="flex-start" spacing={4}>
                    <Box p={3} bg="amber.50" color="#B45309" borderRadius="xl" bg="#FEF3C7">
                      <Icon as={FiPhone} boxSize={5} />
                    </Box>
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">
                        Helplines
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="#162237">
                        Customer & Order Support: +91 98111 23456
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Software Technical Desk: +91 11 2398 7654
                      </Text>
                    </Box>
                  </HStack>

                  <HStack align="flex-start" spacing={4}>
                    <Box p={3} bg="purple.50" color="#7C3AED" borderRadius="xl">
                      <Icon as={FiMail} boxSize={5} />
                    </Box>
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">
                        Email
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="#162237">
                        support@navneetlaw.com
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        orders@navneetlaw.com (Institutional Procurement)
                      </Text>
                    </Box>
                  </HStack>

                  <HStack align="flex-start" spacing={4}>
                    <Box p={3} bg="emerald.50" color="#059669" borderRadius="xl" bg="#D1FAE5">
                      <Icon as={FiClock} boxSize={5} />
                    </Box>
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">
                        Hours of Operation
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="#162237">
                        Monday – Saturday: 9:30 AM – 7:30 PM IST
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        (Aligned with Supreme Court & High Court registry schedules)
                      </Text>
                    </Box>
                  </HStack>
                </VStack>
              </Box>

              {/* Pillars Box */}
              <Box bg="white" p={6} borderRadius="2xl" border="1px solid" borderColor="gray.100" boxShadow="sm">
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider" color="gray.400" mb={4}>
                  Specialized Services
                </Text>
                <SimpleGrid columns={3} spacing={3} textAlign="center">
                  <VStack p={3} bg="#F8FAFC" borderRadius="xl" spacing={2}>
                    <Icon as={FiBookOpen} boxSize={5} color="#162237" />
                    <Text fontSize="xs" fontWeight="bold" color="#162237">
                      Rare Commentaries
                    </Text>
                  </VStack>
                  <VStack p={3} bg="#F8FAFC" borderRadius="xl" spacing={2}>
                    <Icon as={FiMonitor} boxSize={5} color="#162237" />
                    <Text fontSize="xs" fontWeight="bold" color="#162237">
                      SaaS Demos
                    </Text>
                  </VStack>
                  <VStack p={3} bg="#F8FAFC" borderRadius="xl" spacing={2}>
                    <Icon as={FiShield} boxSize={5} color="#162237" />
                    <Text fontSize="xs" fontWeight="bold" color="#162237">
                      100% Authentic
                    </Text>
                  </VStack>
                </SimpleGrid>
              </Box>
            </VStack>
          </Box>

          {/* Right Column: Interactive Inquiry Form */}
          <Box gridColumn={{ base: 'span 1', lg: 'span 7' }}>
            <Box
              bg="white"
              p={{ base: 6, md: 10 }}
              borderRadius="2xl"
              border="1px solid"
              borderColor="gray.100"
              boxShadow="sm"
            >
              <HStack spacing={3} mb={2}>
                <Box p={2} bg="rgba(234, 179, 8, 0.15)" borderRadius="lg" color="#B45309">
                  <Icon as={FiMessageSquare} boxSize={5} />
                </Box>
                <Box>
                  <Heading as="h2" size="md" color="#162237">
                    Send an Official Inquiry
                  </Heading>
                  <Text fontSize="sm" color="gray.500">
                    Our counsel will respond directly to your provided contact details.
                  </Text>
                </Box>
              </HStack>

              <Box as="form" onSubmit={handleSubmit} mt={6}>
                <VStack spacing={5} align="stretch">
                  <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                    <FormControl id="name" isRequired>
                      <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.700">
                        Full Name
                      </FormLabel>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="e.g. Adv. Rajesh Sharma"
                        size="lg"
                        fontSize="sm"
                        borderRadius="xl"
                        bg="#F8FAFC"
                        border="1px solid"
                        borderColor="gray.200"
                        _focus={{ borderColor: '#162237', bg: 'white', boxShadow: 'none' }}
                      />
                    </FormControl>

                    <FormControl id="mobile" isRequired>
                      <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.700">
                        Mobile Number
                      </FormLabel>
                      <Input
                        type="tel"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleInputChange}
                        placeholder="e.g. 9811122334"
                        size="lg"
                        fontSize="sm"
                        borderRadius="xl"
                        bg="#F8FAFC"
                        border="1px solid"
                        borderColor="gray.200"
                        _focus={{ borderColor: '#162237', bg: 'white', boxShadow: 'none' }}
                      />
                    </FormControl>
                  </SimpleGrid>

                  <FormControl id="email" isRequired>
                    <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.700">
                      Email Address
                    </FormLabel>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="e.g. advocate@delhibar.org"
                      size="lg"
                      fontSize="sm"
                      borderRadius="xl"
                      bg="#F8FAFC"
                      border="1px solid"
                      borderColor="gray.200"
                      _focus={{ borderColor: '#162237', bg: 'white', boxShadow: 'none' }}
                    />
                  </FormControl>

                  <FormControl id="inquiryType">
                    <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.700">
                      Subject / Department
                    </FormLabel>
                    <Select
                      name="inquiryType"
                      value={formData.inquiryType}
                      onChange={handleInputChange}
                      size="lg"
                      fontSize="sm"
                      borderRadius="xl"
                      bg="#F8FAFC"
                      border="1px solid"
                      borderColor="gray.200"
                      _focus={{ borderColor: '#162237', bg: 'white', boxShadow: 'none' }}
                    >
                      <option value="General Inquiry">General Legal Inquiry</option>
                      <option value="Book Order Status">Book Orders & Bulk Shipping</option>
                      <option value="Software Demo">Legal Tech Software Demo & Activation</option>
                      <option value="Advocate Apparel">Advocate Robes & Tailoring</option>
                      <option value="Institutional Quotation">Law Firm / University Library Procurement</option>
                    </Select>
                  </FormControl>

                  <FormControl id="message" isRequired>
                    <FormLabel fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.700">
                      Your Message / Citation Requirements
                    </FormLabel>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Specify edition, author, software module, or questions regarding your order..."
                      rows={5}
                      fontSize="sm"
                      borderRadius="xl"
                      bg="#F8FAFC"
                      border="1px solid"
                      borderColor="gray.200"
                      _focus={{ borderColor: '#162237', bg: 'white', boxShadow: 'none' }}
                    />
                  </FormControl>

                  <Button
                    type="submit"
                    size="lg"
                    height="56px"
                    bg="#162237"
                    color="white"
                    borderRadius="xl"
                    fontWeight="bold"
                    fontSize="sm"
                    rightIcon={<FiSend />}
                    isLoading={isSubmitting}
                    loadingText="Transmitting..."
                    _hover={{ bg: '#0B132B', transform: 'translateY(-2px)', boxShadow: 'md' }}
                    _active={{ bg: '#0B132B' }}
                    transition="all 0.2s"
                  >
                    Submit Official Inquiry
                  </Button>
                  <Text fontSize="xs" color="gray.400" textAlign="center">
                    All communications are treated with strict confidentiality adhering to professional legal conduct standards.
                  </Text>
                </VStack>
              </Box>
            </Box>
          </Box>
        </SimpleGrid>
      </Container>
    </Box>
  );
}

export default Contactus;
